import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { reviewsAPI } from "./reviewsAPI";
import {
  validateReviewTitle,
  validateReviewContent,
  sanitizeInput,
  sanitizeInputLight,
  sanitizeTextContent,
  sanitizeTextContentLight,
} from "../../lib/sanitizer";

export default function ReviewForm({ school, onSuccess, onCancel }) {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    ratings: {
      teaching: 0,
      facilities: 0,
      activities: 0,
      environment: 0,
    },
    school: school?._id || school?.id || "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const ratingLabels = {
    teaching: "Chất lượng giảng dạy",
    facilities: "Cơ sở vật chất",
    activities: "Hoạt động ngoại khóa",
    environment: "Môi trường học tập",
  };

  const ratingDescriptions = {
    teaching: "Đội ngũ giảng viên, phương pháp giảng dạy, chương trình học",
    facilities: "Phòng học, thư viện, phòng thí nghiệm, thiết bị học tập",
    activities: "Câu lạc bộ, hoạt động thể thao, sự kiện văn hóa",
    environment: "Không gian học tập, mối quan hệ bạn bè, văn hóa trường",
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Sử dụng sanitize nhẹ cho quá trình gõ, không trim hoặc normalize spaces
    let sanitizedValue = value;
    if (name === "title") {
      sanitizedValue = sanitizeInputLight(value);
    } else if (name === "content") {
      sanitizedValue = sanitizeTextContentLight(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleRatingChange = (category, rating) => {
    setFormData((prev) => ({
      ...prev,
      ratings: {
        ...prev.ratings,
        [category]: rating,
      },
    }));

    // Clear error when user selects rating
    if (errors.ratings) {
      setErrors((prev) => ({
        ...prev,
        ratings: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate school ID
    if (!formData.school || formData.school.trim() === "") {
      newErrors.school = "Thông tin trường học không hợp lệ";
    }

    // Validate title with enhanced sanitization
    const titleValidation = validateReviewTitle(formData.title);
    if (!titleValidation.isValid) {
      newErrors.title = titleValidation.error;
    }

    // Validate content with enhanced sanitization
    const contentValidation = validateReviewContent(formData.content);
    if (!contentValidation.isValid) {
      newErrors.content = contentValidation.error;
    }

    // Check if all 4 categories are rated (backend requirement)
    const { teaching, facilities, activities, environment } = formData.ratings;
    if (
      teaching === 0 ||
      facilities === 0 ||
      activities === 0 ||
      environment === 0
    ) {
      newErrors.ratings = "Vui lòng đánh giá đủ 4 tiêu chí";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      // Sanitize the form data before sending
      const cleanFormData = {
        ...formData,
        title: sanitizeInput(formData.title),
        content: sanitizeTextContent(formData.content),
        school: formData.school.trim(),
      };

      const result = await reviewsAPI.createReview(cleanFormData);

      if (result.success) {
        if (onSuccess) {
          onSuccess(result.data);
        } else {
          navigate(`/schools/${school?._id || school?.id}`);
        }
      } else {
        setErrors({ submit: result.error });
      }
    } catch (error) {
      setErrors({ submit: "Đã xảy ra lỗi không mong muốn" });
    } finally {
      setLoading(false);
    }
  };

  const renderStarRating = (category, currentRating) => {
    return (
      <div className="review-form__rating-stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => handleRatingChange(category, star)}
            className={`review-form__star ${
              star <= currentRating ? "active" : ""
            }`}
            aria-label={`Đánh giá ${star} sao`}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </button>
        ))}
        <span className="review-form__rating-value">
          {currentRating > 0 ? `${currentRating}/5` : "Chưa đánh giá"}
        </span>
      </div>
    );
  };

  const calculateOverallRating = () => {
    const { teaching, facilities, activities, environment } = formData.ratings;
    if (
      teaching === 0 ||
      facilities === 0 ||
      activities === 0 ||
      environment === 0
    ) {
      return 0;
    }
    return ((teaching + facilities + activities + environment) / 4).toFixed(1);
  };

  const getOverallRatingText = (rating) => {
    if (rating === 0) return "Chưa đánh giá";
    if (rating >= 4.5) return "Xuất sắc";
    if (rating >= 4) return "Tốt";
    if (rating >= 3) return "Khá";
    if (rating >= 2) return "Trung bình";
    return "Kém";
  };

  const overallRating = calculateOverallRating();

  // Check if school is valid before rendering
  if (!school || (!school._id && !school.id)) {
    return (
      <div className="review-form">
        <div className="review-form__error">
          <div className="review-form__error-message">
            <span className="review-form__error-icon">⚠️</span>
            <p>Thông tin trường học không hợp lệ. Vui lòng thử lại.</p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="review-form__btn review-form__btn--secondary"
          >
            Quay lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="review-form">
      <div className="review-form__header">
        <div className="review-form__school-info">
          <h1 className="review-form__title">Đánh giá trường học</h1>
          <div className="review-form__school-details">
            <h2 className="review-form__school-name">{school?.name}</h2>
            <p className="review-form__school-address">{school?.address}</p>
          </div>
        </div>
        <div className="review-form__intro">
          <p>
            Chia sẻ trải nghiệm của bạn để giúp những người khác đưa ra quyết
            định tốt hơn. Đánh giá của bạn sẽ giúp cộng đồng hiểu rõ hơn về chất
            lượng giáo dục tại trường này.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="review-form__form">
        {/* Submit Error */}
        {errors.submit && (
          <div className="review-form__submit-error">
            <span className="review-form__error-icon">❌</span>
            {errors.submit}
          </div>
        )}

        {/* School validation error */}
        {errors.school && (
          <div className="review-form__submit-error">
            <span className="review-form__error-icon">⚠️</span>
            {errors.school}
          </div>
        )}

        {/* Overall Rating Display */}
        <div className="review-form__overall-section">
          <div className="review-form__overall-rating">
            <div className="review-form__overall-score">
              <span className="review-form__overall-number">
                {overallRating}
              </span>
              <span className="review-form__overall-max">/5</span>
            </div>
            <div className="review-form__overall-text">
              <h3>Đánh giá tổng thể</h3>
              <p className="review-form__overall-status">
                {getOverallRatingText(overallRating)}
              </p>
            </div>
          </div>
          <p className="review-form__overall-note">
            Điểm tổng thể được tính tự động từ 4 tiêu chí đánh giá bên dưới
          </p>
        </div>

        {/* Category Ratings */}
        <div className="review-form__ratings-section">
          <h3 className="review-form__section-title">
            <span className="review-form__section-icon">⭐</span>
            Đánh giá chi tiết
          </h3>
          <div className="review-form__ratings-grid">
            {Object.entries(ratingLabels).map(([category, label]) => (
              <div key={category} className="review-form__rating-item">
                <div className="review-form__rating-header">
                  <h4 className="review-form__rating-label">{label}</h4>
                  <span className="review-form__rating-required">*</span>
                </div>
                <p className="review-form__rating-description">
                  {ratingDescriptions[category]}
                </p>
                {renderStarRating(category, formData.ratings[category])}
              </div>
            ))}
          </div>
          {errors.ratings && (
            <div className="review-form__error-message">
              <span className="review-form__error-icon">⚠️</span>
              {errors.ratings}
            </div>
          )}
        </div>

        {/* Title */}
        <div className="review-form__field">
          <label className="review-form__label">
            <span className="review-form__label-text">Tiêu đề đánh giá</span>
            <span className="review-form__required">*</span>
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className={`review-form__input ${
              errors.title ? "review-form__input--error" : ""
            }`}
            placeholder="Ví dụ: Trải nghiệm học tập tuyệt vời tại trường ABC"
            maxLength={200}
          />
          <div className="review-form__field-footer">
            {errors.title && (
              <span className="review-form__error">{errors.title}</span>
            )}
            <span className="review-form__character-count">
              {formData.title.length}/200 ký tự
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="review-form__field">
          <label className="review-form__label">
            <span className="review-form__label-text">Nội dung đánh giá</span>
            <span className="review-form__required">*</span>
          </label>
          <textarea
            name="content"
            value={formData.content}
            onChange={handleInputChange}
            className={`review-form__textarea ${
              errors.content ? "review-form__textarea--error" : ""
            }`}
            placeholder="Chia sẻ chi tiết về trải nghiệm của bạn tại trường này... "
            maxLength={2000}
          />
          <div className="review-form__field-footer">
            {errors.content && (
              <span className="review-form__error">{errors.content}</span>
            )}
            <span className="review-form__character-count">
              {formData.content.length}/2000 ký tự
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="review-form__actions">
          <button
            type="button"
            onClick={onCancel || (() => navigate(-1))}
            className="review-form__btn review-form__btn--secondary"
            disabled={loading}
          >
            Hủy bỏ
          </button>
          <button
            type="submit"
            disabled={loading || calculateOverallRating() === 0}
            className="review-form__btn review-form__btn--primary"
          >
            {loading ? (
              <>
                <span className="review-form__btn-spinner"></span>
                Đang xử lý...
              </>
            ) : (
              <>Đăng đánh giá</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
